import { Provider } from 'components/Tooltip'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'designSystem/components/Table'
import { NaturalLanguageResponseSegment } from 'features/chatbot/types/response'
import React from 'react'
import ReactMarkdown, { type Components } from 'react-markdown'
import remarkGfm from 'remark-gfm'
import styled from 'styled-components'

import { CitationTooltip } from './CitationTooltip'

const CODE_BG = 'rgba(255, 255, 255, 0.08)'
const PRE_BG = 'rgba(0, 0, 0, 0.25)'
const BLOCKQUOTE_BORDER = 'rgba(255, 255, 255, 0.2)'
const BLOCKQUOTE_TEXT = 'rgba(255, 255, 255, 0.7)'
const LINK_FALLBACK = '#007BFF'
const DIVIDER = 'rgba(255, 255, 255, 0.15)'

interface AIResponseMessageProps {
  segments: NaturalLanguageResponseSegment[]
}

const ResponseContainer = styled.div`
  color: ${({ theme }) => theme.forecast.defaultText};
  font-size: 0.5625rem;
`

const MarkdownContent = styled.div`
  line-height: 1.5;
  font-size: 0.75rem;

  p {
    font-size: 0.75rem;
    margin: 0 0 0.5em 0;
    &:last-child {
      margin-bottom: 0;
    }
  }

  code {
    background-color: ${CODE_BG};
    padding: 0.1em 0.2em;
    border-radius: 3px;
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
    font-size: 0.9em;
  }

  pre {
    background-color: ${PRE_BG};
    padding: 0.5em;
    border-radius: 4px;
    overflow-x: auto;
    margin: 0.5em 0;

    code {
      background-color: transparent;
      padding: 0;
    }
  }

  ul,
  ol {
    display: block;
    margin: 0.25em 0;
    padding-left: 1.5em;
    list-style-position: outside;
  }

  ul {
    list-style-type: disc;
  }

  ol {
    list-style-type: decimal;
  }

  li {
    display: list-item;
    margin: 0.15em 0;
  }

  li > p {
    margin: 0;
  }

  strong {
    font-weight: 500;
  }

  em {
    font-style: italic;
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    margin: 0.5em 0 0.25em 0;
    font-weight: 500;
    line-height: 1.3;
    font-size: 1em;
  }

  blockquote {
    border-left: 2px solid ${BLOCKQUOTE_BORDER};
    margin: 0.5em 0;
    padding-left: 0.75em;
    color: ${BLOCKQUOTE_TEXT};
  }

  a {
    color: ${({ theme }) => theme.forecast.primary ?? LINK_FALLBACK};
    text-decoration: underline;
  }

  hr {
    border: none;
    border-top: 1px solid ${DIVIDER};
    margin: 0.5em 0;
  }
`

const SegmentContainer = styled.div`
  margin: 0 0 0.5em 0;

  &:last-child {
    margin-bottom: 0;
  }
`

const markdownComponents: Components = {
  table: ({ children }) => <Table layout='prose'>{children}</Table>,
  thead: ({ children }) => <TableHeader layout='prose'>{children}</TableHeader>,
  tbody: ({ children }) => <TableBody>{children}</TableBody>,
  tr: ({ children }) => <TableRow layout='prose'>{children}</TableRow>,
  th: ({ children }) => (
    <TableHead layout='prose' size='compact'>
      {children}
    </TableHead>
  ),
  td: ({ children }) => (
    <TableCell layout='prose' size='compact'>
      {children}
    </TableCell>
  ),
}

export const AIResponseMessage: React.FC<AIResponseMessageProps> = ({
  segments,
}) => {
  return (
    <Provider delayDuration={200}>
      <ResponseContainer>
        {segments.map((segment, segmentIdx) => {
          const citations = segment.citations || []
          return (
            <SegmentContainer key={segmentIdx}>
              <MarkdownContent>
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={markdownComponents}>
                  {segment.text}
                </ReactMarkdown>
              </MarkdownContent>
              {citations.length > 0 && (
                <>
                  {citations.map((citation) => (
                    <CitationTooltip
                      key={`${citation.entryId}`}
                      citation={citation}
                    />
                  ))}
                </>
              )}
            </SegmentContainer>
          )
        })}
      </ResponseContainer>
    </Provider>
  )
}
